import { type FunctionComponent, useCallback, useState } from "react";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import { Input } from "../../../components/ui/input";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Plus, XIcon } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { AccountType } from "../../../constants/account-types.constants";
import { UsersTableComponents } from "./user-table.component.tsx";
import useUsersStore from "../../../store/users.store";
import { useSearchUsersHttp } from "../../../hooks/http/admin/users/search-users.admin.http";
import { debounce, handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";

type AccountTabs = AccountType | "all" | "search";

export const DashboardUsersAdminPartial: FunctionComponent = () => {
  const [search, setSearch] = useState<string>("");
  const [currentTab, setCurrentTab] = useState<AccountTabs>("all");
  const [typing, setTyping] = useState(false);
  const { toast } = useToast();
  const { setData } = useUsersStore();
  const {
    cancel: cancelSearchUsersHttp,
    execute: searchUsersHttp,
    fetching,
  } = useSearchUsersHttp();

  const debouncedSearch = useCallback(
    debounce(async (searchValue: string) => {
      const searchString = searchValue.trim();
      if (!searchString) {
        return;
      }
      const response = await searchUsersHttp({
        body: null,
        queryParams: {
          query: searchString,
        },
      });
      if (!response.success) {
        return handleHttpError(response, toast);
      }
      setData(response.data.users);
      setTyping(false);
    }, 500),
    [],
  );

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    cancelSearchUsersHttp();
    setSearch(e.target.value);
    debouncedSearch(e.target.value);
    setTyping(true);
    setCurrentTab("search");
    if (!e.target.value.trim().length) {
      setCurrentTab("all");
    }
  }

  return (
    <div className="flex flex-col justify-start items-center bg-background p-8 w-full h-screen text-primary container">
      <div className="flex justify-between items-center px-5 py-6 w-full">
        <p className="font-semibold text-2xl">Memed users</p>
        <div className="flex items-center gap-4 py-4">
          <div className="relative flex items-center bg-bginput py-2 pl-5 border border-border rounded-10">
            <SearchIcon />
            <Input
              value={search}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="bg-transparent border-none w-96 font-semibold text-base placeholder:text-secondary"
              placeholder="Search by name and email"
            />

            {search.trim().length > 0 && (
              <XIcon
                onClick={() => {
                  setSearch("");
                  setCurrentTab("all");
                }}
                className="right-5 absolute cursor-pointer"
              />
            )}
          </div>
          <Link to="/admin/users/invite-admin">
            <Button className="flex gap-2 bg-primarybuttonbg px-10 py-7 font-semibold text-base text-black">
              <Plus className="w-4 h-4" /> Invite an admin
            </Button>
          </Link>
        </div>
      </div>

      <div className="w-full">
        <Tabs
          value={currentTab}
          onValueChange={(value) => {
            setCurrentTab(value as unknown as AccountTabs);
          }}
          className=""
        >
          <TabsList className="">
            <TabsTrigger className="border-x-0 border-t-0" value="all">
              All users
            </TabsTrigger>
            <TabsTrigger
              className="border-x-0 border-t-0"
              value={AccountType.ADMIN}
            >
              Admins
            </TabsTrigger>
            <TabsTrigger
              className="border-x-0 border-t-0"
              value={AccountType.BRAND}
            >
              Brands
            </TabsTrigger>
            <TabsTrigger
              className="border-x-0 border-t-0"
              value={AccountType.EPHEMERAL}
            >
              Ephemeral
            </TabsTrigger>

            {search.trim().length ? (
              <TabsTrigger className="border-x-0 border-t-0" value="search">
                Search Result
              </TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="all" className="w-full">
            <UsersTableComponents />
          </TabsContent>
          <TabsContent value={AccountType.ADMIN} className="w-full">
            <UsersTableComponents accountType={AccountType.ADMIN} />
          </TabsContent>
          <TabsContent value={AccountType.BRAND} className="w-full">
            <UsersTableComponents accountType={AccountType.BRAND} />
          </TabsContent>
          <TabsContent value={AccountType.EPHEMERAL} className="w-full">
            <UsersTableComponents accountType={AccountType.EPHEMERAL} />
          </TabsContent>
          {search.trim().length ? (
            <TabsContent value="search" className="w-full">
              <UsersTableComponents
                searchFetching={fetching || typing}
                tableType="search"
              />
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
};
